import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Link,
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import Cottey_Icon from '../../assets/Cottey_Icon+Type_CMYK.png';
import Image from 'mui-image';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`cottey-tabpanel-${index}`}
    aria-labelledby={`cottey-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const CotteyLandingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Hero Section with Cottey Logo */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #FDCA00 0%, #002D72 100%)',
          color: 'white',
          borderRadius: 3,
          p: 3,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Image
            src={Cottey_Icon}
            alt="Cottey College Logo"
            sx={{ maxWidth: '100px' }}
            width={'40vw'}
          />
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 'bold', mb: 1.5, color: '#fff' }}
        >
          Cottey College
        </Typography>
        <Typography
          variant="h5"
          sx={{ mb: 1.5, color: '#fff', fontWeight: 500 }}
        >
          Supported by Women for Women
        </Typography>
        <Typography
          variant="body1"
          sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', color: '#fff' }}
        >
          Cottey College, a nationally ranked, fully accredited, independent,
          liberal arts and sciences college for women located in Nevada,
          Missouri, has been supported by P.E.O. since 1927 and offers
          baccalaureate degrees in a variety of majors. Cottey College, a
          debt-free institution, welcomes women from around the world.
        </Typography>
      </Paper>

      {/* Tab Navigation */}
      <Card sx={{ boxShadow: 3, borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Cottey page tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                minHeight: 64,
              },
            }}
          >
            <Tab label="Information & Resources" />
          </Tabs>
        </Box>

        {/* Information & Resources Tab */}
        <TabPanel value={tabValue} index={0}>
          {/* About Cottey College */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="About Cottey College"
              sx={{
                background: 'linear-gradient(135deg, #FDCA00 0%, #002D72 100%)',
                color: '#fff',
                borderBottom: '1px solid #e0e0e0',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid
                  item
                  container
                  xs={12}
                  justifyContent="center"
                  alignItems="center"
                  direction="column"
                  sx={{ mb: 3 }}
                >
                  <Image
                    src={
                      'https://www.peointernational.org/wp-content/uploads/bb-plugin/cache/Cottey-College-Main-Hall-panorama-6ececbbc062dd8b91404ad08de25271f-8eun14fthm2v.jpg'
                    }
                    alt="Cottey College"
                    sx={{ maxWidth: '700px', borderRadius: 2 }}
                    width={'60vw'}
                  />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <Link
                    href="https://www.cottey.edu/"
                    target="_blank"
                    rel="noopener"
                    sx={{ textDecoration: 'none' }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        px: 4,
                        py: 1.5,
                        background:
                          'linear-gradient(135deg, #FDCA00 0%, #002D72 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        boxShadow: 3,
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        color: '#fff',
                      }}
                    >
                      Visit Cottey College
                    </Button>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Become a Luminary Chapter */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 4,
            }}
          >
            <CardHeader
              title="Become a Luminary Chapter"
              sx={{
                background: 'linear-gradient(135deg, #FDCA00 0%, #002D72 100%)',
                borderBottom: '1px solid #e0e0e0',
                color: '#fff',
              }}
              titleTypographyProps={{ variant: 'h5', fontWeight: 'bold' }}
            />
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    A luminary is one who inspires others and lights the way for
                    those who follow. Local chapters have an opportunity to earn
                    the Cottey College Luminary Chapter designation,
                    representing the light that leads to incredible futures.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Local chapters can build their own chapter legacy by
                    supporting future generations of women learners. By making a
                    lump-sum contribution of $500 or more, a chapter can proudly
                    bear the title of "Cottey College Luminary Chapter." This
                    recognition not only highlights a chapter's dedication, but
                    also gives participating P.E.O. chapters a way to showcase
                    their commitment to empowering women in their higher
                    education endeavors.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    To become a Luminary Chapter, make sure your chapter's
                    contribution reaches your state, provincial or district
                    (s/p/d) treasurer or executive assistant by January 31.
                    (Chapters in Hawaii should send their contributions to the
                    International Chapter Treasurer.)
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    Individuals can also play a pivotal role in this legacy
                    journey. Lump-sum donations of $500 or more qualify your
                    chapter as a Luminary Chapter. Specify your chapter's name
                    on the Individual Donation Form to create this legacy for
                    your chapter. To be considered a charitable donation, this
                    contribution must be made payable to Cottey College and be
                    sent to the college by January 31.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body1" sx={{ p: 1.5, lineHeight: 1.6 }}>
                    S/P/D chapter gift transmittals that include donations
                    qualifying for the Luminary Chapter program must be received
                    by Cottey College in Nevada, Missouri, no later than
                    February 28/29 to be recognized at the upcoming s/p/d
                    convention. Gifts received by Cottey College after February
                    28/29 will be processed and recognized at the following
                    year's convention.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>
      </Card>
    </Container>
  );
};

export default CotteyLandingPage;
